import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const SyllableClapGame = ({ gameData, onComplete }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [clapCount, setClapCount] = useState(0);
    const [completedWords, setCompletedWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 
    const [showSyllables, setShowSyllables] = useState(false);

    const currentWord = gameData?.words?.[currentWordIndex];
    const totalWords = gameData?.words?.length || 0;
    const pointsPerWord = gameData?.points_per_word || 15;

    const handleClap = () => {
        if (feedback) return;
        const newClapCount = clapCount + 1;
        setClapCount(newClapCount);
        triggerHaptic(40);
    };

    const checkAnswer = () => {
        if (!currentWord || feedback || clapCount === 0) return;

        if (clapCount === currentWord.syllables) {
            setFeedback('correct');
            const newScore = score + pointsPerWord;
            setScore(newScore);
            if (!completedWords.includes(currentWord.id)) {
                setCompletedWords(prev => [...prev, currentWord.id]);
            }
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setClapCount(0);
                setShowSyllables(false);
                if (currentWordIndex < totalWords - 1) {
                    setCurrentWordIndex(prev => prev + 1);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setClapCount(0);
            }, 2000);
        }
    };

    const skipWord = () => {
        if (feedback) return;
        triggerHaptic(20);
        setClapCount(0);
        setFeedback(null);
        setShowSyllables(false);
        if (currentWordIndex < totalWords - 1) {
            setCurrentWordIndex(prev => prev + 1);
        }
    };

    if (!gameData || !gameData.words || !currentWord) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">👏</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Ruošiami plojimai...</h3>
            </div>
        );
    }

    const progress = (currentWordIndex / totalWords) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        👏
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Skiemenuoti Plojimai
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ritmas</p>
                        <p className="text-sm md:text-2xl font-black text-purple-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žodis</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentWordIndex + 1}/{totalWords}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/60 backdrop-blur-xl relative overflow-hidden flex flex-col items-center justify-center min-h-[300px]">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none">✨</div>
                <div className="text-8xl md:text-[150px] drop-shadow-2xl animate-float mb-6 md:mb-12">{currentWord.emoji}</div>
                <div className="text-center space-y-4">
                    <h2 className="text-3xl md:text-7xl font-black text-slate-800 tracking-tighter uppercase leading-none">{currentWord.word}</h2>
                    <div className="bg-purple-100 text-purple-600 px-6 py-2 rounded-full text-[10px] md:text-sm font-black uppercase tracking-widest inline-block shadow-inner">
                        {currentWord.category}
                    </div>
                </div>

                {showSyllables && (
                    <div className="mt-8 md:mt-12 w-full bg-indigo-50 border-4 border-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 text-center animate-pop-in shadow-xl">
                        <p className="text-[8px] md:text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em] mb-4">Skiemenuotė</p>
                        <p className="text-xl md:text-5xl font-black text-indigo-900 tracking-[0.2em] uppercase leading-none">{currentWord.syllable_breakdown}</p>
                    </div>
                )}
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-4xl mx-auto py-4">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center justify-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                <div className="w-full relative z-10 flex flex-col items-center gap-6 md:gap-12">
                    
                    <div className="w-full bg-slate-800/80 rounded-2xl md:rounded-[3rem] p-6 md:p-12 shadow-inner border-4 border-dashed border-slate-700 text-center flex flex-col items-center justify-center min-h-[200px] md:min-h-[300px]">
                        <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.5em] mb-6">Tavo Plojimo Ritmas</h4>
                        <div className="text-5xl md:text-[120px] font-black text-white leading-none tracking-tighter drop-shadow-[0_0_30px_rgba(168,85,247,0.3)] animate-pop-in">
                            {clapCount}
                        </div>
                        <div className="mt-6 md:mt-10 flex flex-wrap justify-center gap-3 md:gap-4">
                            {Array.from({ length: clapCount }).map((_, i) => (
                                <div key={i} className="text-2xl md:text-5xl animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>👏</div>
                            ))}
                        </div>
                    </div>

                    <div className="w-full space-y-4 md:space-y-8">
                        <button
                            onClick={handleClap}
                            disabled={!!feedback}
                            className="premium-button-3d w-full py-8 md:py-14 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-2xl md:rounded-[3.5rem] font-black text-2xl md:text-5xl shadow-2xl border-b-8 md:border-b-[12px] border-purple-900 active:translate-y-2 active:border-b-0 transition-all uppercase tracking-widest"
                        >
                            PLOK! 👏
                        </button>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                            <button
                                onClick={checkAnswer}
                                disabled={clapCount === 0 || !!feedback}
                                className="py-4 md:py-8 bg-emerald-500 text-white rounded-xl md:rounded-[2rem] font-black text-xs md:text-xl border-b-4 md:border-b-8 border-emerald-800 shadow-lg disabled:opacity-20"
                            >
                                TIKRINTI ✓
                            </button>
                            <button
                                onClick={() => { triggerHaptic(30); setClapCount(0); setFeedback(null); }}
                                disabled={!!feedback}
                                className="py-4 md:py-8 bg-slate-800 text-slate-400 rounded-xl md:rounded-[2rem] font-black text-xs md:text-xl border-b-4 md:border-b-8 border-slate-900"
                            >
                                NAUJAS 🔄
                            </button>
                            <button
                                onClick={() => { triggerHaptic(20); setShowSyllables(!showSyllables); }}
                                className={`py-4 md:py-8 rounded-xl md:rounded-[2rem] font-black text-xs md:text-xl border-b-4 md:border-b-8 ${showSyllables ? 'bg-indigo-600 text-white border-indigo-900' : 'bg-slate-800 text-indigo-400 border-slate-900'}`}
                            >
                                PAGALBA 💡
                            </button>
                            <button
                                onClick={skipWord}
                                disabled={!!feedback}
                                className="py-4 md:py-8 bg-slate-800 text-slate-600 rounded-xl md:rounded-[2rem] font-black text-xs md:text-xl border-b-4 md:border-b-8 border-slate-900 opacity-50"
                            >
                                TOLIAU ⏭️
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '👏' : '👂'}
                        </div>
                        
                        <div className="text-center space-y-6 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLUS RITMAS!' : 'KLAIDA!'}
                            </h2>

                            <div className={`p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-4 text-center italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed">
                                    {feedback === 'correct' 
                                        ? `Puiku! Žodis "${currentWord.word}" turi ${currentWord.syllables} skiemenis: ${currentWord.syllable_breakdown}.` 
                                        : `Oi! Panašu, kad suplojai ne tiek kartų, kiek reikia.`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SyllableClapGame;
